/**
 * Client per POST /api/conversations/stream (SSE).
 * Eventi: chunk (testo), progress (pipeline), done, error.
 */
import type { ConversationProgressEvent } from './conversationStreamEvents';
const getStreamBase = () => {
  const base = process.env.REACT_APP_API_URL || '';
  return `${base.replace(/\/$/, '')}/api/conversations`;
};

const getStreamUrl = () => `${getStreamBase()}/stream`;
const getStreamContinueUrl = () => `${getStreamBase()}/stream/continue`;

export type StreamDonePayload = {
  conversationId: string;
  message: {
    id: string;
    createdAt?: string;
    text: string;
    table?: unknown;
    attachments?: Array<{
      kind: string;
      filename: string;
      mimeType: string;
      content: string;
    }> | null;
  } | null;
  metadata?: Record<string, unknown>;
};

export type StreamCallbacks = {
  onChunk: (delta: string) => void;
  onProgress?: (event: ConversationProgressEvent) => void;
  onDone: (payload: StreamDonePayload) => void;
  onError: (message: string) => void;
};

function handleSseData(
  currentEvent: string,
  data: unknown,
  callbacks: StreamCallbacks,
  fallbackConversationId?: string,
): void {
  if (currentEvent === 'chunk' && data && typeof data === 'object' && 'delta' in data) {
    const delta = (data as { delta?: string }).delta;
    if (delta != null) callbacks.onChunk(delta);
    return;
  }
  if (currentEvent === 'progress' && data && typeof data === 'object' && 'type' in data) {
    callbacks.onProgress?.(data as ConversationProgressEvent);
    return;
  }
  if (currentEvent === 'done' && data && typeof data === 'object') {
    const d = data as {
      conversationId?: string;
      message?: StreamDonePayload['message'];
      metadata?: Record<string, unknown>;
    };
    callbacks.onDone({
      conversationId: d.conversationId ?? fallbackConversationId ?? '',
      message: d.message
        ? {
            id: d.message.id,
            createdAt: d.message.createdAt,
            text: d.message.text ?? '',
            table: d.message.table ?? undefined,
            attachments: d.message.attachments ?? undefined,
          }
        : null,
      metadata: d.metadata,
    });
    return;
  }
  if (currentEvent === 'error' && data && typeof data === 'object') {
    callbacks.onError(
      (data as { message?: string }).message ?? 'Errore sconosciuto',
    );
  }
}

function parseSseLines(
  lines: string[],
  callbacks: StreamCallbacks,
  state: { currentEvent: string },
  fallbackConversationId?: string,
): void {
  for (const line of lines) {
    if (line.startsWith('event: ')) {
      state.currentEvent = line.slice(7).trim();
    } else if (line.startsWith('data: ') && state.currentEvent) {
      try {
        const data = JSON.parse(line.slice(6));
        handleSseData(state.currentEvent, data, callbacks, fallbackConversationId);
      } catch {
        // ignora righe data non JSON
      }
      state.currentEvent = '';
    }
  }
}

export async function streamConversation(
  userInput: string,
  currentDate: string,
  token: string | null,
  callbacks: StreamCallbacks,
): Promise<void> {
  if (!token) {
    callbacks.onError('Non autenticato');
    return;
  }

  const url = getStreamUrl();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ userInput, currentDate }),
  });

  if (!res.ok) {
    const text = await res.text();
    callbacks.onError(text || `HTTP ${res.status}`);
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    callbacks.onError('Stream non disponibile');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  const state = { currentEvent: '' };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      parseSseLines(lines, callbacks, state);
    }
  } finally {
    reader.releaseLock();
  }
}

/** Stream per continue (secondo messaggio e seguenti): stesso formato chunk + progress + done. */
export async function streamContinueConversation(
  conversationId: string,
  userInput: string,
  currentDate: string,
  token: string | null,
  callbacks: StreamCallbacks,
): Promise<void> {
  if (!token) {
    callbacks.onError('Non autenticato');
    return;
  }

  const url = getStreamContinueUrl();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ conversationId, userInput, currentDate }),
  });

  if (!res.ok) {
    const text = await res.text();
    callbacks.onError(text || `HTTP ${res.status}`);
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    callbacks.onError('Stream non disponibile');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  const state = { currentEvent: '' };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      parseSseLines(lines, callbacks, state, conversationId);
    }
  } finally {
    reader.releaseLock();
  }
}
